/**
 * A panel somebody can put where they want it.
 *
 * The editor grew a fixed left column and a fixed right column, which works
 * until the work does not fit the columns: a list of two hundred things in a
 * panel eight lines tall, or a details pane squeezed under a map nobody is
 * looking at. Every panel is now a window that can be dragged, resized, rolled
 * up and put away, and remembers where it was left.
 *
 * Deliberately small. No docking, no tabs, no snapping. It is a box with a bar
 * at the top, which is all the job needs. The one thing it does have is a
 * stacking order, and only because it has to: with seven of these on a screen
 * there is always one under another, and clicking a window that stays buried
 * reads as a broken window rather than as a design decision. Which one is in
 * front is not this component's business, though: it is told, and it says when
 * it was touched.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { CORNERS, CORNER_CURSORS, resizeFrom, type Corner } from '../resize';

export interface WindowRect {
  x: number;
  y: number;
  width: number;
  height: number;
  folded?: boolean;
}

/** Nothing narrower or shorter than this, or the bar itself stops working. */
const MIN_WIDTH = 180;
const MIN_HEIGHT = 90;

export function EditorWindow({
  title,
  note,
  rect,
  onRect,
  onClose,
  children,
  className = '',
  resizable = true,
  fits = false,
  bare = false,
  depth,
  onRaise,
}: {
  title: string;
  /** A word or two beside the title: a count, a state, whatever the panel knows. */
  note?: ReactNode;
  rect: WindowRect;
  onRect: (rect: WindowRect) => void;
  /** Left out for a window that is always there, such as the tools. */
  onClose?: () => void;
  children: ReactNode;
  className?: string;
  resizable?: boolean;
  /**
   * Only as big as what is in it, both ways.
   *
   * For a window whose content is a handful of buttons rather than a list: the
   * tools panel showed four rows in a box half a screen tall, and the empty two
   * thirds of it covered the world for no reason. It takes the width it needs
   * as well, so a bar of tools is as long as the tools are and no longer.
   * Nothing to resize in such a window either, so it carries no grip.
   */
  fits?: boolean;
  /**
   * No bar and no body padding: for a window that is all content, such as the
   * compact toolbar, which carries its own grip instead.
   */
  bare?: boolean;
  /** How high this one sits, which is decided by whoever was touched last. */
  depth?: number;
  onRaise?: () => void;
}): JSX.Element {
  // What is being dragged and where the pointer was when it last said. Held as
  // state rather than in a ref so the listeners below are always looking at the
  // same numbers the window was drawn with.
  const [drag, setDrag] = useState<{
    kind: 'move' | 'size';
    /** Which corner is being pulled, for a resize. */
    corner: Corner;
    pointerX: number;
    pointerY: number;
  } | null>(null);

  // Listened for on the window rather than on the panel, so a pointer that runs
  // off the edge of it keeps dragging instead of dropping it where it left.
  useEffect(() => {
    if (!drag) return;

    // Moved by however far the pointer has come since it last reported, so the
    // window keeps up with it however many frames go by in between.
    const move = (event: PointerEvent): void => {
      const dx = event.clientX - drag.pointerX;
      const dy = event.clientY - drag.pointerY;
      setDrag({ ...drag, pointerX: event.clientX, pointerY: event.clientY });

      if (drag.kind === 'move') {
        onRect({
          ...rect,
          x: Math.min(Math.max(rect.x + dx, 0), window.innerWidth - 80),
          y: Math.min(Math.max(rect.y + dy, 0), window.innerHeight - 40),
        });
        return;
      }

      // Any corner, and the one opposite it stays where it is. Written as
      // arithmetic somewhere else so it can be checked without a browser.
      onRect({
        ...rect,
        ...resizeFrom(rect, drag.corner, dx, dy, {
          minWidth: MIN_WIDTH,
          minHeight: MIN_HEIGHT,
        }),
      });
    };

    const stop = (): void => setDrag(null);

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);

    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, [drag, rect, onRect]);

  const begin =
    (kind: 'move' | 'size', corner: Corner = 'bottomRight') =>
    (event: React.PointerEvent): void => {
      // Left button only, and never from the buttons in the bar.
      if (event.button !== 0) return;

      event.preventDefault();
      onRaise?.();
      setDrag({ kind, corner, pointerX: event.clientX, pointerY: event.clientY });
    };

  const folded = rect.folded === true;

  // Which half of the screen it is in decides which side the grip sits on.
  const gripSide =
    typeof window !== 'undefined' && rect.x > window.innerWidth / 2 ? 'left' : 'right';

  return (
    <section
      className={`editor-window ${className}${folded ? ' folded' : ''}${bare ? ' bare' : ''}`}
      style={{
        left: `${rect.x}px`,
        top: `${rect.y}px`,
        width: bare || fits ? undefined : `${rect.width}px`,
        height: bare || folded || fits ? undefined : `${rect.height}px`,
        zIndex: depth,
      }}
      // Held anywhere with Alt, which is how a window dragged under the bar or
      // off the edge is got back: its own bar may be the one part of it that
      // cannot be reached.
      onPointerDown={(event) => {
        onRaise?.();
        if (!event.altKey) return;
        begin('move')(event);
      }}
    >
      {bare ? null : (
        <header className="editor-window-bar" onPointerDown={begin('move')}>
          <span className="editor-window-title">{title}</span>
          {note !== undefined ? <span className="editor-window-note">{note}</span> : null}

          <button
            type="button"
            className="editor-window-button"
            title={folded ? 'Roll it back down' : 'Roll it up'}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => onRect({ ...rect, folded: !folded })}
          >
            {folded ? '▢' : '▁'}
          </button>

          {onClose ? (
            <button
              type="button"
              className="editor-window-button"
              title="Put it away"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={onClose}
            >
              ✕
            </button>
          ) : null}
        </header>
      )}

      {/* A bare window has no bar to drag by, so it carries a ribbed grip on
          whichever side faces the middle of the screen. Pushed against the right
          edge the grip is on the left, and the other way round, so it can never
          end up as the part that is off screen. */}
      {bare ? (
        <div className={`editor-window-ribs ${gripSide}`} onPointerDown={begin('move')} />
      ) : null}

      {folded ? null : (
        <div className={bare ? 'editor-window-bare' : 'editor-window-body'}>{children}</div>
      )}

      {/* One on each corner. Which one is pulled decides which edges move, so
          a window against the right of the screen can be widened leftwards
          rather than pushed off it. */}
      {folded || !resizable || bare || fits
        ? null
        : CORNERS.map((corner) => (
            <div
              key={corner}
              className={`editor-window-grip ${corner}`}
              title="Drag to resize"
              style={{ cursor: CORNER_CURSORS[corner] }}
              onPointerDown={begin('size', corner)}
            />
          ))}
    </section>
  );
}
